/**
 * Easing functions for PiP camera transition animations.
 *
 * All functions map a normalised progress value t ∈ [0, 1] to an eased
 * progress value also in [0, 1], where f(0) = 0 and f(1) = 1.
 */

import type { PiPEasingType } from '@/worker/types';

/**
 * Apply the chosen easing curve to a normalised time value t ∈ [0, 1].
 *
 * @param t       Raw linear progress (0 = start, 1 = end). Values outside
 *                [0, 1] are clamped before the easing is applied.
 * @param easing  Which curve to use.
 * @returns       Eased progress value in [0, 1].
 */
export function applyEasing(t: number, easing: PiPEasingType): number {
  // Clamp to valid range
  const tc = Math.max(0, Math.min(1, t));

  switch (easing) {
    case 'linear':
      return tc;

    case 'ease_in':
      // Quadratic ease-in: starts slow, accelerates
      return tc * tc;

    case 'ease_out':
      // Quadratic ease-out: starts fast, decelerates
      return 1 - (1 - tc) * (1 - tc);

    case 'ease_in_out':
      // Quadratic ease-in-out: slow at both ends, fast in the middle
      return tc < 0.5 ? 2 * tc * tc : 1 - 2 * (1 - tc) * (1 - tc);

    case 'exponential':
    default: {
      // Exponential decay lerp — preserves the frame-rate-independent "damped
      // spring" feel of the legacy implementation, mapped onto a [0,1] range.
      //
      // Raw formula: 1 - exp(-k * t)  reaches ~0.9933 at t=1 with k=5.
      // We renormalise by dividing by the value at t=1 so the output exactly
      // reaches 1.0 at the end, then clamp for safety.
      const k = 5;
      const ceiling = 1 - Math.exp(-k);
      return Math.min(1, (1 - Math.exp(-k * tc)) / ceiling);
    }
  }
}
